import { downloadXlsx } from '@/lib/export';
import type { AccountRef, VoucherListItem } from '@ruby/shared';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import type { ComboOption } from '@/components/ui/combobox';
import { ApiError, api } from '@/lib/api';


export interface PartnerPick {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  isCustomer: boolean;
  isSupplier: boolean;
  /** The party's own account (code and title), when it has one. */
  account?: { code: string; name: string } | null;
}

/** Postable accounts for voucher lines (cached; the chart changes rarely). */
export function usePostableAccounts() {
  return useQuery({ queryKey: ['accounting', 'postable'], queryFn: () => api<AccountRef[]>('/accounting/accounts/postable'), staleTime: 5 * 60_000 });
}

export const accountOptions = (list: AccountRef[] | undefined, current?: string | null, filter?: (a: AccountRef) => boolean): ComboOption[] =>
  (list ?? [])
    .filter((a) => (a.isActive || a.id === current) && (!filter || filter(a)))
    .map((a) => ({ value: a.id, label: `${a.code} – ${a.name}`, hint: a.requiresParty ? 'Customer / supplier account' : undefined }));

/**
 * Customer / supplier search. In Accounting (`accounts`) a party is shown by its own account — chart-of-accounts code
 * and title (R10, owner) — never by its customer / supplier code; other modules keep the party code.
 */
export const searchPartners = (role?: 'customer' | 'supplier', opts: { accounts?: boolean } = {}) => async (q: string): Promise<ComboOption[]> => {
  const rows = await api<PartnerPick[]>('/accounting/partners', { query: { search: q || undefined, role } });
  return rows.map((p) => ({
    value: p.id,
    label: opts.accounts ? (p.account ? `${p.account.code} – ${p.account.name}` : p.name) : `${p.code} – ${p.name}`,
    hint: [p.isCustomer && 'Customer', p.isSupplier && 'Supplier', opts.accounts && !p.account && 'No own account (one-time)', !p.isActive && 'Inactive'].filter(Boolean).join(' · '),
  }));
};
/** A party as Accounting names it: its own account's code and title, else its name (R10). */
export const partyLabel = (p: { name: string; account?: { code: string; name: string } | null }) => (p.account ? `${p.account.code} – ${p.account.name}` : p.name);

const aed = new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** 1,234.50 — negatives in brackets, zero as a dash. */
export const money = (n: number | null | undefined, dash = true) => (n == null || (dash && Math.abs(n) < 0.005) ? '—' : n < 0 ? `(${aed.format(-n)})` : aed.format(n));
/** Debit-positive balance shown with Dr / Cr. */
export const drcr = (n: number) => (Math.abs(n) < 0.005 ? '—' : `${aed.format(Math.abs(n))} ${n > 0 ? 'Dr' : 'Cr'}`);

export const DOC_TYPE_LABEL: Record<string, string> = {
  'acct.journal': 'Journal',
  'acct.contra': 'Contra',
  'acct.receipt': 'Receipt',
  'acct.payment': 'Payment',
  'acct.pdc_received': 'PDC received',
  'acct.pdc_issued': 'PDC issued',
  'acct.opening': 'Opening',
  'acct.legacy_voucher': 'Legacy voucher',
};

/** Voucher types by prefix, in the order users look for them. */
export const VOUCHER_TYPES: { prefix: string; label: string; short: string }[] = [
  { prefix: 'CPV', label: 'Cash payment', short: 'Cash pay' },
  { prefix: 'BPV', label: 'Bank payment', short: 'Bank pay' },
  { prefix: 'CRV', label: 'Cash receipt', short: 'Cash rcpt' },
  { prefix: 'BRV', label: 'Bank receipt', short: 'Bank rcpt' },
  { prefix: 'PDCP', label: 'Cheque issued (PDC)', short: 'PDC issued' },
  { prefix: 'PDCR', label: 'Cheque received (PDC)', short: 'PDC rcvd' },
  { prefix: 'JV', label: 'Journal', short: 'Journal' },
  { prefix: 'CTR', label: 'Contra (cash ↔ bank)', short: 'Contra' },
  { prefix: 'LSV', label: 'Sales (old system)', short: 'Sales' },
  { prefix: 'LSR', label: 'Sales return (old system)', short: 'Sales ret.' },
  { prefix: 'LPV', label: 'Purchase (old system)', short: 'Purchase' },
  { prefix: 'LPR', label: 'Purchase return (old system)', short: 'Purch. ret.' },
  { prefix: 'OB', label: 'Opening balances', short: 'Opening' },
];
export const voucherTypeLabel = (prefix: string, docType?: string) => VOUCHER_TYPES.find((t) => t.prefix === prefix)?.label ?? (docType ? DOC_TYPE_LABEL[docType] : undefined) ?? prefix;

/** Downloads rows as CSV (Excel opens it). */
/** Report export: a real Excel workbook (kept under the old name so every report shares it). */
export function downloadCsv(name: string, header: string[], rows: (string | number | null)[][]) {
  void downloadXlsx(name, header, rows);
}

/** Money in or out, by voucher type (↓ green / ↑ red in the lists). */
const IN_PREFIX = ['CRV', 'BRV', 'PDCR'];
const OUT_PREFIX = ['CPV', 'BPV', 'PDCP'];
export const direction = (v: Pick<VoucherListItem, 'prefix'>) => (IN_PREFIX.includes(v.prefix ?? '') ? 'in' : OUT_PREFIX.includes(v.prefix ?? '') ? 'out' : null);

/** The colour of a voucher's type pill: cash, bank, PDC, journal, purchase, salary. */
export const typeTone = (v: Pick<VoucherListItem, 'prefix' | 'docType'>) => {
  const p = v.prefix ?? '';
  if (v.docType === 'hr.payroll_run') return 'bg-mod-mattress/12 text-mod-mattress';
  if (['CRV', 'CPV'].includes(p)) return 'bg-st-done/12 text-st-done';
  if (['BRV', 'BPV'].includes(p)) return 'bg-st-progress/12 text-st-progress';
  if (p.startsWith('PDC')) return 'bg-st-qc/12 text-st-qc';
  if (['JV', 'CTR'].includes(p)) return 'bg-st-hold/12 text-st-hold';
  if (['LPV', 'LPR', 'IPV'].includes(p)) return 'bg-mod-furniture/12 text-mod-furniture';
  return 'bg-surface-2 text-fg-muted';
};

/** The server's field names in words, so a refused save says which box to fix. */
const FIELD_WORDS: Record<string, string> = {
  docDate: 'Voucher date', narration: 'Narration', reference: 'Reference', cashAccountId: 'Bank / cash account', mode: 'Received / paid by',
  partnerId: 'Customer / supplier', chequeNo: 'Cheque number', chequeDate: 'Cheque date', drawerBank: "Customer's bank", bankAccountId: 'Our bank (cheque drawn on)',
  replacesChequeId: 'Returned cheque being replaced', amount: 'Amount', discount: 'Discount', allocations: 'Invoices / bills ticked', employeeId: 'Employee',
  loanId: 'Advance / loan', installment: 'Monthly deduction', startMonth: 'Deductions start', accountId: 'Account', description: 'Line description', lines: 'Lines',
};
const fieldWords = (path: string, lineNumbers: boolean) => {
  const parts = path.split('.');
  const row = parts.findIndex((x) => /^\d+$/.test(x));
  const last = [...parts].reverse().find((x) => !/^\d+$/.test(x)) ?? path;
  const label = FIELD_WORDS[last] ?? last.replace(/Id$/, '').replace(/([a-z])([A-Z])/g, '$1 $2');
  return lineNumbers && row > 0 && parts[0] === 'lines' ? `Line ${Number(parts[row]) + 1} · ${label}` : label;
};

/**
 * Why a save failed, in words: each field the server named (with its label), else the server's reason, else what went
 * wrong in the browser — never a bare "something went wrong".
 */
export function saveProblems(e: unknown, lineNumbers = false): string[] {
  if (e instanceof ApiError) {
    const fields = (e.problem.errors ?? []).filter((f) => f.message).map((f) => {
      const label = f.path ? fieldWords(f.path, lineNumbers) : '';
      return label && !f.message.toLowerCase().includes(label.toLowerCase()) ? `${label}: ${f.message}` : f.message;
    });
    return fields.length ? fields : [e.message || `The server refused it (error ${e.status}).`];
  }
  if (e instanceof TypeError && /fetch|network|load failed/i.test(e.message)) return ['The server could not be reached — check the connection, then press the button again (it will not be saved twice).'];
  return [`Unexpected problem on this screen: ${e instanceof Error ? e.message : String(e)}. Tell the administrator; the details are in the browser console.`];
}

/** The same, as one line for a dialog's message. */
export const saveProblemsText = (e: unknown) => saveProblems(e).join(' · ');

/** Every row a list has shown on its pages, so rows ticked on several pages can still be exported as ticked. */
export function useSeenRows<T extends { id: string }>(items: T[]) {
  const seen = useRef(new Map<string, T>());
  for (const r of items) seen.current.set(r.id, r);
  return seen.current;
}

/**
 * The ticked rows for an export, in list order of ticking; null means "every row matching the filter", which is also
 * right after "select all matching" (the same set).
 */
export const tickedRows = <T,>(seen: Map<string, T>, ids: string[], matching: boolean): T[] | null => {
  if (!ids.length || matching) return null;
  const rows = ids.map((id) => seen.get(id));
  return rows.every(Boolean) ? (rows as T[]) : null;
};
